import { useCallback } from "react";

import useTypedContext from "hooks/useTypedContext";
import { AccountContext } from "views/AccountWrapper";
import { Action, WorkerPool } from "types/generated";
import { hasAtLeastOneRequiredAction } from "utils/access";
import { AccessResult, NO_ACCESS_MESSAGE } from "utils/access";

const WORKER_POOL_ACCESS_TYPES = [Action.SpaceAdmin, Action.WorkerPoolReset];

/**
 * Hook that returns whether current user can reset worker pools
 * User needs to have SpaceAdmin or WorkerPoolReset actions in at least one space
 * @returns Function that takes a worker pool and returns AccessResult
 */
const useCanResetWorkerPool = (): ((workerPool: WorkerPool) => AccessResult) => {
  const { viewer } = useTypedContext(AccountContext);

  return useCallback(
    (workerPool: WorkerPool): AccessResult => {
      if (viewer?.admin) {
        return { result: true };
      }

      const hasAccess = hasAtLeastOneRequiredAction(
        WORKER_POOL_ACCESS_TYPES,
        viewer,
        workerPool.spaceDetails.id
      );

      if (hasAccess) {
        return { result: true };
      }

      return { result: false, message: NO_ACCESS_MESSAGE };
    },
    [viewer]
  );
};

export default useCanResetWorkerPool;
